import { add } from "ol/coordinate";
import { Method } from "../Config";
import { ResponseType, Gateway } from "./Gateway";

export interface AddressValidateReqBody {
	query?: string;
}

export interface GeocodingQueryParams {
	query: string;
	index: string;
}

export interface MultiFullGeocodingReqBody {
	addresses: {
		index: number;
		query: string;
	}[];
}

export interface RevGeocodingQueryParams {
	coord: string;
}

class RequestGateway extends Gateway {
	protected readonly _contextPath = "request";
	protected readonly _pathParams?: { [key: string]: string };
	protected readonly _processParams = {
		validator: "validator", // mapdata 주소판별 process
		getfeature: "getfeature",
		geocoding: "geocoding",
		mgeocoding: "mgeocoding",
		revgeocoding: "revgeocoding",
	};

	public async reqAddressValidate(body?: AddressValidateReqBody): Promise<ResponseType> {
		const queryParam = {
			process: this._processParams.validator,
		};

		return await this.request(Method.POST, undefined, queryParam, body);
	}

	public async reqGeocoding(param: GeocodingQueryParams) {
		const queryParam = {
			process: this._processParams.geocoding,
			...param,
		};

		const response = await this.request(Method.POST, undefined, queryParam);
		if (response.flag) {
			this.getGeocodingResult(response);
		}
		return response;
	}

	public async reqMultiFullGeocoding(body?: MultiFullGeocodingReqBody): Promise<ResponseType> {
		const queryParam = {
			process: this._processParams.mgeocoding,
		};
		const response = await this.request(Method.POST, undefined, queryParam, body);
		if (response.flag) {
			this.getMultiFullGeocodingResult(response);
		}

		return response;
	}

	private getMultiFullGeocodingResult(response: ResponseType) {
		const xmlParser = new DOMParser();
		const document = xmlParser.parseFromString(response.result, "text/xml");
		const ACGC_LIST = document.getElementsByTagName("ACGC_LIST");

		if (ACGC_LIST.length) {
			response.result = [];

			const ACGCs = ACGC_LIST[0].getElementsByTagName("ACGC") || 0;
			for (let i = 0; i < ACGCs.length; ++i) {
				const ACGC = ACGCs[i];
				const ac_result = ACGC.getElementsByTagName("AC_RESULT")[0];
				const index = ACGC.getElementsByTagName("INDEX")[0];
				if (ac_result) {
					// GC_MATCH_STAUS 파싱 (exact/similar)
					const matchStatusElement = ACGC.getElementsByTagName("GC_MATCH_STAUS")[0];
					const matchStatus = matchStatusElement?.textContent?.trim() || "unknown";
					const item: { address: string | null; coords: number[] | null; matchStatus: string } = {
						address: null,
						coords: null,
						matchStatus: matchStatus,
					};

					if (matchStatus === "exact") {
						const sido = ac_result.getElementsByTagName("SIDO")[0];

						// 도로명주소가 존재하는 경우
						if (sido && sido.textContent) {
							const sigun = ac_result.getElementsByTagName("SIGUN")[0];
							const gu = ac_result.getElementsByTagName("GU")[0];
							const emd = ac_result.getElementsByTagName("EMD")[0];
							const ri = ac_result.getElementsByTagName("RI")[0];
							const roadName = ac_result.getElementsByTagName("ROADNAME")[0];
							const majorNum = ac_result.getElementsByTagName("MAJOR_NUM")[0];
							const minorNum = ac_result.getElementsByTagName("MINOR_NUM")[0];
							const detailAddress = ac_result.getElementsByTagName("DETAIL_ADDRESS")[0];

							const txt = (el: Element) => (el?.textContent ?? "").trim();

							const houseNumber = (() => {
								const major = txt(majorNum);
								const minor = txt(minorNum);
								if (!major) return "";
								return minor ? `${major}-${minor}` : major;
							})();

							const address = [txt(sido), txt(sigun), txt(gu), txt(emd), txt(ri), txt(roadName), houseNumber, txt(detailAddress)].filter((v) => v !== "").join(" ");

							item.address = address;
						}
						// 도로명 주소가 없는 경우
						else {
							item.address = ac_result.getElementsByTagName("JIBUN_ADDRESS")[0]?.textContent;
						}
					} else if (matchStatus === "similar") {
						const addressElement = ac_result.getElementsByTagName("SIMILAR_ADDRESS")[0];
						if (addressElement && addressElement.textContent) {
							item.address = addressElement.textContent;
						}
					}

					const coordsNode = ac_result.getElementsByTagName("COORDS")[0];
					if (coordsNode) {
						const coordXElement = coordsNode.getElementsByTagName("COORDX")[0];
						const coordYElement = coordsNode.getElementsByTagName("COORDY")[0];

						if (coordXElement && coordYElement && coordXElement.textContent && coordYElement.textContent) {
							// 문자열을 숫자로 변환하여 배열에 저장
							item.coords = [Number(coordXElement.textContent), Number(coordYElement.textContent)];
						}
					}
					response.result.push({ index: index.textContent, data: item });
				} else {
					response.result.push({ index: index.textContent, data: null });
				}
			}
		}

		return response;
	}

	private getGeocodingResult(response: ResponseType): { [key: string]: any } {
		const xmlParser = new DOMParser();
		const document = xmlParser.parseFromString(response.result, "text/xml");
		const place = document.getElementsByTagName("ACGC");
		// 결과가 있는 경우
		if (place.length > 0) {
			response.result = {};

			const flag = document.getElementsByTagName("GC_RESULT")[0];

			// 지오코딩 결과 여부 파싱
			const flagText = flag?.textContent?.trim();
			response.flag = flagText?.toLowerCase() === "true";

			if (response.flag) {
				const result = place[0].getElementsByTagName("AC_RESULT");

				if (result.length > 0) {
					result[0].childNodes.forEach((node: ChildNode) => {
						// console.log(node);
						if (node.nodeName === "SIMILAR_ADDRESS") {
							response.result.address = node.textContent;
						} else if (node.nodeName === "COORDS") {
							const coords = node.childNodes;
							response.result.coords = [Number(coords[0].textContent), Number(coords[1].textContent)];
						}
					});
				}
			}
		}
		return response;
	}

	public async reqRevGeocoding(param: RevGeocodingQueryParams): Promise<ResponseType> {
		const queryParam = {
			process: this._processParams.revgeocoding,
			...param,
		};

		return await this.request(Method.GET, undefined, queryParam);
	}
}

export default RequestGateway;
